import { UserRole } from "@features/settings/types/settings.types";
import ModalBlank from "@shared/components/modals/ModalBlank";
import PaginationNumeric from "@shared/components/PaginationNumeric";
import { useProfile } from "@shared/hooks/useProfile";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CategoryForm from "../components/CategoryForm";
import CategoryList from "../components/CategoryList";
import DayEvents from "../components/DayEvents";
import EventCard from "../components/EventCard";
import EventForm from "../components/EventForm";
import { useCalendar } from "../hooks/useCalendar";
import {
  CalendarEvent,
  CreateCategoryData,
  CreateEventData,
  EventCategory,
  UpdateCategoryData,
  UpdateEventData,
} from "../types/calendar.types";

const CalendarPage: React.FC = () => {
  const { user } = useProfile();
  const isAdmin = user?.role === UserRole.ADMIN;

  const calendar = useCalendar();
  const eventsPerPage = 8;

  const today = new Date();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [daysInMonth, setDaysInMonth] = useState<number[]>([]);
  const [startingBlankDays, setStartingBlankDays] = useState<number[]>([]);
  const [endingBlankDays, setEndingBlankDays] = useState<number[]>([]);

  // Modal states
  const [showEventModal, setShowEventModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const [selectedCategory, setSelectedCategory] =
    useState<EventCategory | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Tabs: "calendar" | "cards"
  const [activeTab, setActiveTab] = useState<"calendar" | "cards">("cards");

  // Pagination state for cards view
  const [currentPage, setCurrentPage] = useState(1);

  // Search term state
  const [searchTerm, setSearchTerm] = useState("");

  // Reset currentPage to 1 if filters/search/tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, calendar.activeCategory, activeTab]);

  // Calculate days in month
  const getDays = () => {
    const days = new Date(year, month + 1, 0).getDate();

    // Starting empty cells (previous month)
    const startingDayOfWeek = new Date(year, month, 1).getDay();
    let startingBlankDaysArray = [];
    for (let i = 1; i <= startingDayOfWeek; i++) {
      startingBlankDaysArray.push(i);
    }

    // Ending empty cells (next month)
    const endingDayOfWeek = new Date(year, month + 1, 0).getDay();
    let endingBlankDaysArray = [];
    for (let i = 1; i < 7 - endingDayOfWeek; i++) {
      endingBlankDaysArray.push(i);
    }

    // Current month cells
    let daysArray = [];
    for (let i = 1; i <= days; i++) {
      daysArray.push(i);
    }

    setStartingBlankDays(startingBlankDaysArray);
    setEndingBlankDays(endingBlankDaysArray);
    setDaysInMonth(daysArray);
  };

  // Check if a date is today
  const isToday = (date: number): boolean => {
    const day = new Date(year, month, date);
    return today.toDateString() === day.toDateString();
  };

  // Get calendar.events for a specific date
  const getEvents = (date: number): CalendarEvent[] => {
    const day = new Date(year, month, date);
    return calendar.events.filter((e) => {
      const eventDate = e.startDate.toDate();
      return (
        eventDate.getDate() === day.getDate() &&
        eventDate.getMonth() === day.getMonth() &&
        eventDate.getFullYear() === day.getFullYear() &&
        (!calendar.activeCategory || e.categoryId === calendar.activeCategory)
      );
    });
  };

  // Get color for an event based on its category
  const eventColor = (categoryId: string): string => {
    const category = calendar.categories.find((cat) => cat.id === categoryId);
    return category?.color || "gray";
  };

  // Handle event creation
  const handleCreateEvent = async (data: CreateEventData) => {
    try {
      setIsSubmitting(true);
      await calendar.createEvent(data);
    } catch (error) {
      console.error("Error creating event:", calendar.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle event update
  const handleUpdateEvent = async (data: UpdateEventData) => {
    if (!selectedEvent) return;

    try {
      setIsSubmitting(true);
      await calendar.updateEvent(selectedEvent.id, data);
    } catch (error) {
      console.error("Error updating event:", calendar.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle event deletion
  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      setIsSubmitting(true);
      await calendar.deleteEvent(selectedEvent.id);
      setShowEventModal(false);
      setShowEventDetailsModal(false);
    } catch (error) {
      console.error("Error deleting event:", calendar.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle category creation
  const handleCreateCategory = async (data: CreateCategoryData) => {
    try {
      setIsSubmitting(true);
      await calendar.createCategory(data);
    } catch (error) {
      console.error("Error creating category:", calendar.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle category update
  const handleUpdateCategory = async (data: UpdateCategoryData) => {
    if (!selectedCategory) return;

    try {
      setIsSubmitting(true);
      await calendar.updateCategory(selectedCategory.id, data);
    } catch (error) {
      console.error("Error updating category:", calendar.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle category deletion
  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;

    try {
      setIsSubmitting(true);
      await calendar.deleteCategory(selectedCategory.id);
      setShowCategoryModal(false);
    } catch (error) {
      console.error("Error deleting category:", calendar.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open event form modal for creating a new event
  const openCreateEventModal = () => {
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  // Open event form modal for editing an existing event
  const openEditEventModal = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setShowEventModal(true);
    setShowEventDetailsModal(false);
  };

  // Navigate to the event details page
  const navigate = useNavigate();
  const openEventDetailsPage = (event: CalendarEvent) => {
    navigate(`/calendar/event/${event.id}`);
  };

  // Open category form modal for creating a new category
  const openCreateCategoryModal = () => {
    setSelectedCategory(null);
    setShowCategoryModal(true);
  };

  // Open category form modal for editing an existing category
  const openEditCategoryModal = (category: EventCategory) => {
    setSelectedCategory(category);
    setShowCategoryModal(true);
  };

  // Open day calendar.events modal
  const openDayEventsModal = (date: number) => {
    setSelectedDate(new Date(year, month, date));
    setShowDayEventsModal(true);
  };

  // Handle category click for filtering
  const handleCategoryClick = (categoryId: string) => {
    calendar.toggleCategoryFilter(categoryId);
  };

  // Initialize days on mount and when month/year changes
  useEffect(() => {
    1;
    getDays();
  }, [month, year]);

  return (
    <AuthenticatedLayout
      pageHeader={{
        title:
          activeTab === "calendar"
            ? `${monthNames[month]} ${year}`
            : "Discover Events",
        actions: [
          {
            label: "Add Event",
            onClick: openCreateEventModal,
          },
        ],
        search:
          activeTab === "cards"
            ? {
              show: true,
              placeholder: "Search...",
              onChange: setSearchTerm,
            }
            : undefined,
      }}
      isLoading={calendar.loading}
      error={calendar.error ? new Error(calendar.error) : null}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {/* Tabs + Categories */}
        <div className="sm:flex sm:justify-between sm:items-center mb-4">
          {/* Tabs and calendar.categories on the left column */}
          <div className="mb-4 sm:mb-0 mr-2 flex flex-row min-w-[200px] items-center">
            {/* Tabs */}
            <div className="flex mb-0">
              <button
                className={`px-4 py-2 rounded-t-lg font-medium focus:outline-none transition border-b-2 ${activeTab === "cards"
                  ? "border-[#0070CC] text-[#0070CC] dark:text-[#0070CC]"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-[#0070CC]"
                  }`}
                onClick={() => setActiveTab("cards")}
              >
                Card View
              </button>
              <button
                className={`ml-2 px-4 py-2 rounded-t-lg font-medium focus:outline-none transition border-b-2 ${activeTab === "calendar"
                  ? "border-[#0070CC] text-[#0070CC] dark:text-[#0070CC]"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-[#0070CC]"
                  }`}
                onClick={() => setActiveTab("calendar")}
              >
                Calendar View
              </button>
            </div>
            {/* Categories */}
            <div className="ml-4">
              <CategoryList
                categories={calendar.categories}
                activeCategory={calendar.activeCategory}
                onCategoryClick={handleCategoryClick}
                onEditCategory={isAdmin ? openEditCategoryModal : undefined}
                isAdmin={isAdmin}
                onAddCategory={isAdmin ? openCreateCategoryModal : undefined}
              />
            </div>
          </div>
        </div>

        {/* Total calendar.events counter + navigation (calendar only) */}
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm text-gray-500 font-medium">
            {calendar.events.length} Events
          </span>
          {activeTab === "calendar" && (
            <div className="grid grid-flow-col sm:auto-cols-max gap-2">
              {/* Previous month button */}
              <button
                className="btn px-2.5 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 disabled:border-gray-200 dark:disabled:border-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed"
                disabled={month === 0 && year === today.getFullYear() - 10}
                onClick={() => {
                  if (month === 0) {
                    setMonth(11);
                    setYear(year - 1);
                  } else {
                    setMonth(month - 1);
                  }
                }}
              >
                <span className="sr-only">Previous month</span>
                <svg
                  className="fill-current text-gray-400 dark:text-gray-500"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                >
                  <path d="M9.4 13.4l1.4-1.4-4-4 4-4-1.4-1.4L4 8z" />
                </svg>
              </button>

              {/* Next month button */}
              <button
                className="btn px-2.5 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 disabled:border-gray-200 dark:disabled:border-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed"
                disabled={month === 11 && year === today.getFullYear() + 10}
                onClick={() => {
                  if (month === 11) {
                    setMonth(0);
                    setYear(year + 1);
                  } else {
                    setMonth(month + 1);
                  }
                }}
              >
                <span className="sr-only">Next month</span>
                <svg
                  className="fill-current text-gray-400 dark:text-gray-500"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                >
                  <path d="M6.6 13.4L5.2 12l4-4-4-4 1.4-1.4L12 8z" />
                </svg>
              </button>

              <hr className="w-px h-full bg-gray-200 dark:bg-gray-700/60 border-none mx-1" />

              {/* Today button */}
              <button
                className="btn bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
                onClick={() => {
                  setMonth(today.getMonth());
                  setYear(today.getFullYear());
                }}
              >
                Today
              </button>
            </div>
          )}
        </div>

        {/* Views according to active tab */}
        {activeTab === "calendar" && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
            {/* Days of the week */}
            <div className="grid grid-cols-7 gap-px border-b border-gray-200 dark:border-gray-700/60">
              {dayNames.map((day) => (
                <div className="px-1 py-3" key={day}>
                  <div className="text-gray-500 text-sm font-medium text-center lg:hidden">
                    {day.substring(0, 3)}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400 text-sm font-medium text-center hidden lg:block">
                    {day}
                  </div>
                </div>
              ))}
            </div>

            {/* Day cells */}
            <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700/60">
              {/* Diagonal stripes pattern */}
              <svg className="sr-only">
                <defs>
                  <pattern
                    id="stripes"
                    patternUnits="userSpaceOnUse"
                    width="5"
                    height="5"
                    patternTransform="rotate(135)"
                  >
                    <line
                      className="stroke-current text-gray-200 dark:text-gray-700 opacity-50"
                      x1="0"
                      y="0"
                      x2="0"
                      y2="5"
                      strokeWidth="2"
                    />
                  </pattern>
                </defs>
              </svg>

              {/* Empty cells (previous month) */}
              {startingBlankDays.map((blankday) => (
                <div
                  className="bg-gray-50 dark:bg-gray-800 h-20 sm:h-28 lg:h-36"
                  key={`prev-${blankday}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="100%"
                    height="100%"
                  >
                    <rect width="100%" height="100%" fill="url(#stripes)" />
                  </svg>
                </div>
              ))}

              {/* Days of the current month */}
              {daysInMonth.map((day) => {
                const dayEvents = getEvents(day);

                return (
                  <div
                    className="relative bg-white dark:bg-gray-800 h-20 sm:h-28 lg:h-36 overflow-hidden cursor-pointer"
                    key={`day-${day}`}
                    onClick={() => openDayEventsModal(day)}
                  >
                    <div className="h-full flex flex-col justify-between">
                      {/* Events */}
                      <div className="grow flex flex-col relative p-0.5 sm:p-1.5 overflow-hidden">
                        {dayEvents.slice(0, 2).map((event) => {
                          const color = eventColor(event.categoryId);

                          return (
                            <motion.button
                              key={event.id}
                              className="relative w-full text-left mb-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEventDetailsPage(event);
                              }}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                            >
                              <div
                                className={`px-2 py-0.5 rounded-lg overflow-hidden text-white bg-${color}-500`}
                              >
                                {/* Event name */}
                                <div className="text-xs font-semibold truncate">
                                  {event.title}
                                </div>
                                {/* Event time */}
                                <div className="text-xs uppercase truncate hidden sm:block">
                                  {/* Start date */}
                                  <span>
                                    {event.startDate
                                      .toDate()
                                      .toLocaleTimeString([], {
                                        hour12: true,
                                        hour: "numeric",
                                        minute: "numeric",
                                        timeZone: event.timezone || undefined,
                                      })}
                                  </span>
                                  {/* End date */}
                                  {event.endDate && (
                                    <span>
                                      -{" "}
                                      <span>
                                        {event.endDate
                                          .toDate()
                                          .toLocaleTimeString([], {
                                            hour12: true,
                                            hour: "numeric",
                                            minute: "numeric",
                                            timeZone: event.timezone || undefined,
                                          })}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </motion.button>
                          );
                        })}
                        <div
                          className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-calendar.events-none"
                          aria-hidden="true"
                        ></div>
                      </div>

                      {/* Cell footer */}
                      <div className="flex justify-between items-center p-0.5 sm:p-1.5">
                        {/* More button (if more than 2 calendar.events) */}
                        {dayEvents.length > 2 && (
                          <button
                            className="text-xs text-gray-500 dark:text-gray-300 font-medium whitespace-nowrap text-center sm:py-0.5 px-0.5 sm:px-2 border border-gray-200 dark:border-gray-700/60 rounded-lg"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDayEventsModal(day);
                            }}
                          >
                            <span className="md:hidden">+</span>
                            <span>{dayEvents.length - 2}</span>{" "}
                            <span className="hidden md:inline">more</span>
                          </button>
                        )}

                        {/* Day number */}
                        <button
                          className={`inline-flex ml-auto w-6 h-6 items-center justify-center text-xs sm:text-sm dark:text-gray-300 font-medium text-center rounded-full hover:bg-[#0070CC]/10 dark:hover:bg-gray-600 ${isToday(day) ? "text-[#0070CC]" : ""
                            }`}
                        >
                          {day}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Empty cells (next month) */}
              {endingBlankDays.map((blankday) => (
                <div
                  className="bg-gray-50 dark:bg-gray-800 h-20 sm:h-28 lg:h-36"
                  key={`next-${blankday}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="100%"
                    height="100%"
                  >
                    <rect width="100%" height="100%" fill="url(#stripes)" />
                  </svg>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "cards" && (
          <>
            {/* Search calendar.events */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-4">
              {calendar.events.length === 0 ? (
                <div className="col-span-full text-center text-gray-500 dark:text-gray-300">
                  There are no calendar.events.
                </div>
              ) : (
                (() => {
                  const filteredSortedEvents = calendar.events
                    .filter(
                      (e) =>
                        (!calendar.activeCategory ||
                          e.categoryId === calendar.activeCategory) &&
                        (e.title
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                          (e.description &&
                            e.description
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase())))
                    )
                    .slice()
                    .sort(
                      (a, b) =>
                        b.startDate.toDate().getTime() -
                        a.startDate.toDate().getTime()
                    );

                  // Pagination logic
                  const totalPages = Math.ceil(filteredSortedEvents.length / 8);
                  const paginatedEvents = filteredSortedEvents.slice(
                    (currentPage - 1) * 8,
                    currentPage * 8
                  );

                  return (
                    <>
                      {paginatedEvents.map((event) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          category={calendar.categories.find(
                            (cat) => cat.id === event.categoryId
                          )}
                          onClick={openEventDetailsPage}
                          userId={user?.id || ""}
                          fetchCalendarData={calendar.fetchCalendarData}
                        />
                      ))}
                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="flex justify-center mt-8 space-x-2 col-span-full">
                          <PaginationNumeric
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                          />
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </>
        )}
      </div>

      {/* Event Form Modal */}
      <ModalBlank
        id="event-form-modal"
        className="max-h-[80vh] overflow-y-auto p-4"
        modalOpen={showEventModal}
        setModalOpen={setShowEventModal}
        showCloseButton={true}
      >
        {/* <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full p-6"></div> */}
        <EventForm
          event={selectedEvent || undefined}
          categories={calendar.categories}
          onSubmit={selectedEvent ? handleUpdateEvent : handleCreateEvent}
          onDelete={
            selectedEvent &&
              user &&
              (selectedEvent.createdBy === user.id || isAdmin)
              ? handleDeleteEvent
              : undefined
          }
          onCancel={() => setShowEventModal(false)}
          isSubmitting={isSubmitting}
          canEdit={
            selectedEvent
              ? user && (selectedEvent.createdBy === user.id || isAdmin)
              : true
          }
          canDelete={
            selectedEvent
              ? user && (selectedEvent.createdBy === user.id || isAdmin)
              : false
          }
        />
      </ModalBlank>

      {/* Category Form Modal */}
      <ModalBlank
        id="category-form-modal"
        className="p-6"
        modalOpen={showCategoryModal}
        setModalOpen={setShowCategoryModal}
        showCloseButton={true}
      >
        <CategoryForm
          category={selectedCategory || undefined}
          onSubmit={
            selectedCategory ? handleUpdateCategory : handleCreateCategory
          }
          onDelete={selectedCategory ? handleDeleteCategory : undefined}
          onCancel={() => setShowCategoryModal(false)}
          isSubmitting={isSubmitting}
        />
      </ModalBlank>

      {/* Day Events Modal */}
      <ModalBlank
        id="day-calendar.events-modal"
        className="p-6"
        modalOpen={showDayEventsModal}
        setModalOpen={setShowDayEventsModal}
        showCloseButton={true}
      >
        {selectedDate && (
          <DayEvents
            date={selectedDate}
            events={calendar.getEventsForDate(selectedDate)}
            categories={calendar.categories}
            onEventClick={openEventDetailsPage}
          />
        )}
      </ModalBlank>
    </AuthenticatedLayout>
  );
};

export default CalendarPage;
